import { useEffect, useMemo, useRef } from 'react';
import {
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import {
  ApprovalWorkflowControllerGetAssetByAwParams,
  ApprovalWorkflowControllerListAssetsByAwParams,
  AWAssetDto,
  AssetControllerCountAssetsParams,
  AssetControllerGetAssetParams,
  AssetControllerListAssetsParams,
  AssetItemDto,
  AssetReviewControllerGetAssetByReviewParams,
  CopyAssetsDto,
  MoveAssetToFolderDto,
  PageDto
} from '@api/Api';
import { copyAssets, moveAsset } from '@api/Asset';
import { apiClient } from '@api/client';
import { getItemPlaceholderFromListQuery } from '@hooks/queries/helpers';

export function useAssetQuery(
  params: AssetControllerGetAssetParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: AssetItemDto;
  }
) {
  return useQuery({
    ...(options ?? {}),
    queryKey: ['assets', 'item', params.assetId, params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.asset.assetControllerGetAsset(params, {
        signal
      });
      return data.asset;
    },
    placeholderData: () => {
      if (options?.placeholderData) return options.placeholderData;
      const asset = getItemPlaceholderFromListQuery<AssetItemDto>(
        ['assets', 'list'],
        params.assetId
      );
      if (!asset) return undefined;
      return asset;
    }
  });
}

export function useAssetsQuery(
  params: AssetControllerListAssetsParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: PageDto & {
      edges: {
        node: AssetItemDto;
        cursor: string;
      }[];
    };
  }
) {
  return useInfiniteQuery({
    ...(options ?? {}),
    queryKey: ['assets', 'list', params],
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await apiClient.asset.assetControllerListAssets(
        { ...params, after: pageParam },
        { signal }
      );
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.endCursor : undefined,
    placeholderData: options?.placeholderData
      ? {
          pages: [options.placeholderData],
          pageParams: [
            options.placeholderData.hasNext
              ? options.placeholderData.endCursor
              : undefined
          ]
        }
      : undefined
  });
}

export function useAssetsCountQuery(
  params: AssetControllerCountAssetsParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: number;
  }
) {
  return useQuery({
    ...(options ?? {}),
    queryKey: ['assets', 'count', params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.asset.assetControllerCountAssets(
        params,
        { signal }
      );
      return data.count;
    }
  });
}

export const ASSETS_SLIDER_PAGE_SIZE = 30;

type AssetsSliderPageParam = {
  before?: string;
  after?: string;
  inclusive?: boolean;
};

/**
 * The first or last asset of a list, e.g. to wrap around in the slider when
 * that end is not loaded. The last one is the first in the reversed order (the
 * tie-breaker is not reversed: with equal sort values it is one of those).
 */
export async function fetchAssetsListEdge(
  params: Omit<
    AssetControllerListAssetsParams,
    'after' | 'before' | 'inclusive' | 'limit'
  >,
  edge: 'first' | 'last'
) {
  const orderBy =
    edge === 'last'
      ? params.orderBy?.map(
          (item) =>
            (item.endsWith(':ASC')
              ? item.replace(/:ASC$/, ':DESC')
              : item.replace(/:DESC$/, ':ASC')) as typeof item
        )
      : params.orderBy;
  const { data } = await apiClient.asset.assetControllerListAssets({
    ...params,
    orderBy,
    limit: 1
  });
  return data.edges[0]?.node ?? null;
}

/**
 * Lists assets around `anchorAssetId`, starting from its position in the list
 * and loading pages in both directions. Falls back to the beginning of the list
 * when the anchor is not part of it.
 */
export function useAssetsSliderQuery(
  params: Omit<
    AssetControllerListAssetsParams,
    'after' | 'before' | 'inclusive' | 'limit'
  >,
  anchorAssetId: string | null,
  options?: { enabled?: boolean }
) {
  const enabled = (options?.enabled ?? true) && !!anchorAssetId;

  // The cursor encodes the anchor's sort values (e.g. custom order), so it is
  // refreshed together with asset lists when they are invalidated
  const anchorQuery = useQuery({
    queryKey: ['assets', 'slider-anchor', params, anchorAssetId],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.asset.assetControllerGetAssetsListAnchor(
        { ...params, assetId: anchorAssetId ?? '' },
        { signal }
      );
      return data;
    },
    enabled,
    keepPreviousData: true
  });
  const anchor = anchorQuery.data;

  const listQuery = useInfiniteQuery({
    queryKey: ['assets', 'list', 'slider', params, anchor?.cursor ?? null],
    queryFn: async ({ pageParam, signal }) => {
      const cursorParams: AssetsSliderPageParam =
        pageParam ??
        (anchor?.cursor ? { after: anchor.cursor, inclusive: true } : {});
      const { data } = await apiClient.asset.assetControllerListAssets(
        { ...params, limit: ASSETS_SLIDER_PAGE_SIZE, ...cursorParams },
        { signal }
      );
      return { ...data, isBefore: !!cursorParams.before };
    },
    getNextPageParam: (lastPage): AssetsSliderPageParam | undefined => {
      // Refetching rebuilds pages forward from the first (often a `before`)
      // page. Entries always follow a `before` page, whatever its hasNext is.
      if (lastPage.isBefore) {
        if (lastPage.endCursor) return { after: lastPage.endCursor };
        return anchor?.cursor
          ? { after: anchor.cursor, inclusive: true }
          : undefined;
      }
      return lastPage.hasNext && lastPage.endCursor
        ? { after: lastPage.endCursor }
        : undefined;
    },
    getPreviousPageParam: (firstPage): AssetsSliderPageParam | undefined => {
      // For `before` pages hasNext means there are more entries before them
      const hasPrevious = firstPage.isBefore
        ? firstPage.hasNext
        : !!anchor?.cursor && !!anchor.index;
      return hasPrevious && firstPage.startCursor
        ? { before: firstPage.startCursor }
        : undefined;
    },
    enabled: enabled && anchorQuery.isSuccess,
    keepPreviousData: true
  });

  const pages = listQuery.data?.pages;

  // Load entries before the anchor right away, so it is not the first item
  const { hasPreviousPage, isFetching, isError, fetchPreviousPage } = listQuery;
  const isAwaitingPreviousPage =
    pages?.length === 1 && !pages[0].isBefore && !!hasPreviousPage && !isError;
  useEffect(() => {
    if (!isAwaitingPreviousPage || isFetching) return;
    fetchPreviousPage({ cancelRefetch: false });
  }, [isAwaitingPreviousPage, isFetching, fetchPreviousPage]);

  const items = useMemo(
    () => pages?.flatMap((page) => page.edges.map((edge) => edge.node)) ?? [],
    [pages]
  );

  // The anchor page always starts with the anchor. If it is missing, the
  // cursor is outdated (e.g. the anchor was moved), so locate it again.
  // At most once per list update, to not loop when the cursor does not change.
  const { refetch: refetchAnchor } = anchorQuery;
  const listUpdatedAt = listQuery.dataUpdatedAt;
  const anchorRefetchedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (!anchor?.cursor || !pages || isFetching) return;
    if (items.some((item) => item.id === anchorAssetId)) return;
    if (anchorRefetchedAtRef.current === listUpdatedAt) return;
    anchorRefetchedAtRef.current = listUpdatedAt;
    refetchAnchor({ cancelRefetch: false });
  }, [
    anchor,
    pages,
    isFetching,
    items,
    anchorAssetId,
    listUpdatedAt,
    refetchAnchor
  ]);
  // Position of items[0] in the whole list
  const firstItemIndex = useMemo(() => {
    if (!anchor?.cursor || anchor.index === null || !pages) return 0;
    const beforeCount = pages
      .filter((page) => page.isBefore)
      .reduce((sum, page) => sum + page.edges.length, 0);
    return Math.max(anchor.index - beforeCount, 0);
  }, [anchor, pages]);

  return {
    items,
    firstItemIndex,
    // Only the anchor page is loaded yet, entries before it are on the way
    isAwaitingPreviousPage,
    isLoading: anchorQuery.isLoading || listQuery.isLoading,
    isFetching: anchorQuery.isFetching || listQuery.isFetching,
    hasNextPage: !!listQuery.hasNextPage,
    hasPreviousPage: !!hasPreviousPage,
    fetchNextPage: () => listQuery.fetchNextPage({ cancelRefetch: false }),
    fetchPreviousPage: () => fetchPreviousPage({ cancelRefetch: false }),
    isFetchingNextPage: listQuery.isFetchingNextPage,
    isFetchingPreviousPage: listQuery.isFetchingPreviousPage
  };
}

export function useAssetByAwQuery(
  params: ApprovalWorkflowControllerGetAssetByAwParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: AssetItemDto;
  }
) {
  return useQuery({
    ...(options ?? {}),
    queryKey: ['assets', 'item', params.assetId, 'by-aw', params],
    queryFn: async ({ signal }) => {
      const { data } =
        await apiClient.approvalWorkflow.approvalWorkflowControllerGetAssetByAw(
          params,
          { signal }
        );
      return data.asset;
    },
    placeholderData: () => {
      if (options?.placeholderData) return options.placeholderData;
      const asset = getItemPlaceholderFromListQuery<AssetItemDto>(
        ['assets', 'list'],
        params.assetId
      );
      if (!asset) return undefined;
      return asset;
    }
  });
}

// Asset URLs are signed for 24 hours: the list is refetched (on mount or
// focus) well before that, and not used as placeholders once that old
const AW_ASSETS_STALE_TIME_MS = 6 * 60 * 60 * 1000;
const AW_ASSETS_PLACEHOLDER_MAX_AGE_MS = 12 * 60 * 60 * 1000;

/**
 * All assets of an approval, as placeholder data while one of them loads (the
 * approval itself only has a summary of them). Not refetched with the approval
 * (e.g. after decisions): an opened asset is fetched anyway.
 */
export function useAssetsByAwQuery(
  params: ApprovalWorkflowControllerListAssetsByAwParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['assets', 'by-aw-list', params],
    queryFn: async ({ signal }) => {
      const { data } =
        await apiClient.approvalWorkflow.approvalWorkflowControllerListAssetsByAw(
          params,
          { signal }
        );
      return data.assets;
    },
    enabled: (options?.enabled ?? true) && !!params.awId,
    staleTime: AW_ASSETS_STALE_TIME_MS
  });
}

/** The asset from useAssetsByAwQuery, unless it is outdated */
export function getAwAssetPlaceholder(
  query: ReturnType<typeof useAssetsByAwQuery>,
  awAsset: Pick<AWAssetDto, 'id' | 'versionId'> | undefined
) {
  if (!awAsset || !query.data) return undefined;
  if (Date.now() - query.dataUpdatedAt > AW_ASSETS_PLACEHOLDER_MAX_AGE_MS) {
    return undefined;
  }
  const asset = query.data.find((item) => item.id === awAsset.id);
  // A version was added since (the approval is kept up to date)
  if (asset?.asset?.versions[0]?.id !== awAsset.versionId) return undefined;
  return asset;
}

export function useAssetByReviewQuery(
  params: AssetReviewControllerGetAssetByReviewParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: AssetItemDto;
  }
) {
  return useQuery({
    ...(options ?? {}),
    queryKey: ['assets', 'item', params.assetId, 'by-review', params],
    queryFn: async ({ signal }) => {
      const { data } =
        await apiClient.assetReview.assetReviewControllerGetAssetByReview(
          params,
          { signal }
        );
      return data.asset;
    },
    placeholderData: () => {
      if (options?.placeholderData) return options.placeholderData;
      const asset = getItemPlaceholderFromListQuery<AssetItemDto>(
        ['assets', 'list'],
        params.assetId
      );
      if (!asset) return undefined;
      return asset;
    }
  });
}

export function useCopyAssetsMutation({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: CopyAssetsDto) => {
      return await copyAssets(data);
    },
    onSettled: () => {
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}

export function useMoveAssetsMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: MoveAssetToFolderDto) => {
      return await moveAsset(data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    }
  });
  return mutation;
}
